// React
import React from 'react';
import PropTypes from 'prop-types';

/** @jsx jsx */
import { jsx } from '@emotion/core';

import shortid from 'shortid';
// VDS-React
import Checkbox from '@visa/vds-react/checkbox-styled';
import CheckboxErrorMessage from '@visa/vds-react/checkbox-error-message-styled';
import Icon from '@visa/vds-react/icon-styled';

import stylesBuilder from './component.styles';

const { wrapperStyles, checkboxStyles, errorStyles } = stylesBuilder();

const CybsCheckbox = React.forwardRef(
  (
    {
      checked,
      dataTest,
      disabled,
      i18n,
      isIndeterminate,
      isInvalid,
      name,
      onCheck,
      required,
      ...props
    },
    ref
  ) => {
    const shortId = shortid.generate();
    return (
      <>
        <div css={wrapperStyles} data-test={`${dataTest}-cybsCheckbox`}>
          <Checkbox
            customCSS={theme => checkboxStyles(theme)}
            checked={checked}
            disabled={disabled}
            indeterminate={isIndeterminate}
            invalid={isInvalid}
            label={i18n.label}
            aria-label={i18n.ariaLabel}
            name={name}
            onChange={
              onCheck && !disabled ? e => onCheck(e, e.target.checked) : null
            }
            required={required}
            aria-describedby={shortId}
            {...props}
            ref={ref}
          />
        </div>
        {isInvalid && (
          <CheckboxErrorMessage id={shortId} css={errorStyles}>
            <Icon name="error" resolution="low" />
            {i18n.errorText}
          </CheckboxErrorMessage>
        )}
      </>
    );
  }
);

CybsCheckbox.propTypes = {
  /**
   * If true, the checkbox is checked.
   */
  checked: PropTypes.bool,
  /**
   * Data Test.
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * If true, the checkbox is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * localization information
   */
  i18n: PropTypes.shape({
    errorText: PropTypes.string,
    label: PropTypes.string,
    ariaLabel: PropTypes.string
  }),
  /**
   * Indeterminate state of the checkbox (mi-checked)
   */
  isIndeterminate: PropTypes.bool,
  /**
   * If true, the checkbox or the checkbox list is invalid.
   */
  isInvalid: PropTypes.bool,
  /**
   * name for the radios
   */
  name: PropTypes.string,
  /**
   * Callback fired when the checkbox is checked.
   */
  onCheck: PropTypes.func,
  /**
   * Ref for the checkbox.
   */
  ref: PropTypes.element,
  /**
   * Determines whether checkbox must be checked.
   */
  required: PropTypes.bool
};

CybsCheckbox.defaultProps = {
  checked: false,
  disabled: false,
  i18n: {
    label: '',
    errorText: '',
    ariaLabel: ''
  },
  isIndeterminate: false,
  isInvalid: false,
  name: '',
  onCheck: () => {},
  ref: null,
  required: false
};

CybsCheckbox.displayName = 'CybsCheckbox';

export default CybsCheckbox;
